import moment from "moment";
import defaults from "lodash/defaults";
import Nutrition from "./Nutrition";
import CreatePlanModal from "../../../parts/modals/create-nutrition-plan";
import { MealPlanner } from "./meal-planner/MealPlanner";
import { AddFoodModal } from "./meal-planner/modals/AddFoodModal";
import { HandleFoodModal } from "./meal-planner/modals/HandleFoodModal";
import { CreateNewFoodModal } from "./meal-planner/modals/CreateNewFoodModal";
import { DuplicateMealPlanModal } from "./meal-planner/modals/DuplicateMealPlanModal";
import { MoveMealPlanModal } from "./meal-planner/modals/MoveMealPlanModal";
import { CustomMealModal } from "./meal-planner/modals/CustomMealModal";
import { BuildMealModal } from "./meal-planner/modals/BuildMealModal";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { SaveMealPlanAsTemplateModal } from "./meal-planner/modals/SaveMealPlanAsTemplateModal";
import CopyMealPlanFromTemplateModal from "../../coaching-portal/all-templates/modals/CopyMealPlanFromTemplateModal";
import DataSourceModal from "./meal-planner/modals/DataSourceModal";

const NutritionController = (app) => {
  app.get("/", async ({ req, res, load, locals, api }) => {
    const { query } = req;
    const { section, plan, mealPlan } = query;
    const modal = query.modal || {};
    const { organizationRoleName } = locals;

    defaults(query, {
      date: moment().format("YYYY-MM-DD"),
      utcDate: Number(moment().format("YYYYMMDD")),
    });

    const { athlete, showBack } = query;

    let comesFromBodyComp = false;

    // Required conditional because this could be undefined when refreshing
    if (req?.original?.headers?.referer) {
      comesFromBodyComp =
        req.original.headers.referer.includes("body-composition");
    }
    if (showBack) comesFromBodyComp = true;
    locals.comesFromBodyComp = comesFromBodyComp;

    const isAllowToLoadAllAthletes =
      organizationRoleName &&
      (organizationRoleName === "Nutritionist" ||
        organizationRoleName === "Admin" ||
        organizationRoleName === "Owner");

    await Promise.all([
      load("dailyStats", {
        proxyAthlete: athlete,
      }),
      load("nutritionPlans", {
        proxyAthlete: athlete,
      }),
      load("activeExercises", {
        start: moment().subtract(1, "year"),
        end: moment().add(1, "year"),
        proxyAthlete: athlete,
      }),
      ...(locals.activeUser.organizationId &&
      isAllowToLoadAllAthletes &&
      !locals.allAthletesFromOrganization
        ? [load("allAthletesFromOrganization")]
        : []),
    ]);

    //init of meal plan
    if (section && plan && mealPlan) {
      const mealPlanPromises = [
        load("mealPlan", {
          mealPlanId: mealPlan,

          proxyAthlete: athlete,
        }),
      ];

      if (locals.activeUser?.organizationId) {
        mealPlanPromises.push(
          load("usersOrganization", {
            organizationId: locals.activeUser.organizationId,
          })
        );
      }

      await Promise.all(mealPlanPromises);
      //we are request it after the promise all to have the organization information already loaded

      await Promise.all([
        load("mostUsedMealPlans"),

        load("mealPlanIsTemplate", { mealPlanId: mealPlan }),
      ]);
    }
    //end of meal plan

    if (locals.nutritionPlans) {
      locals.currentNutritionPlan = locals.nutritionPlans.find(
        (plan) =>
          plan.utcStartDate <= query.utcDate && plan.utcEndDate >= query.utcDate
      );
    }

    const editPlan = query.plan
      ? locals.nutritionPlans.find((plan) => plan._id === query.plan)
      : null;

    const copy = query.copy ? true : false;

    let copyPlanDates = {};

    if (editPlan && copy) {
      const lastPlan = locals.nutritionPlans.sort(
        (a, b) => b.utcEndDate - a.utcEndDate
      );

      const todayUtc = moment().format("YYYYMMDD");

      const lastPlanEndDate = moment(String(lastPlan[0].utcEndDate));

      const isBefore = lastPlanEndDate.isBefore(todayUtc);

      const startDay = moment(String(editPlan.utcStartDate)).toDate();
      const endDay = moment(String(editPlan.utcEndDate)).toDate();

      const numberOfDays = moment(endDay).diff(startDay, "days");

      if (isBefore) {
        copyPlanDates = {
          startDate: Number(todayUtc),
          endDate: Number(
            moment(todayUtc).add(numberOfDays, "days").format("YYYYMMDD")
          ),
        };
      } else {
        copyPlanDates = {
          startDate: Number(
            moment(String(lastPlan[0].utcEndDate))
              .add(1, "days")
              .format("YYYYMMDD")
          ),
          endDate: Number(
            moment(String(lastPlan[0].utcEndDate))
              .add(numberOfDays + 1, "days")
              .format("YYYYMMDD")
          ),
        };
      }
    }

    const nextMealPlanPromises = [];
    switch (modal.type) {
      case "copy-meal-plan-from-template":
        nextMealPlanPromises.push(
          load("mealPlanTemplates", {
            customLimit: 1,
            customSkip: 1,
            noPagination: true,
          }),
          load("assignedMealPlans")
        );
        break;
      case "create-meal-planner-food":
      case "build-meal":
        nextMealPlanPromises.push(load("customFoods"));
        break;
      default:
        break;
    }
    await Promise.all(nextMealPlanPromises);
    locals.modal = {
      "create-nutrition-plan": (
        <CreatePlanModal
          editPlan={editPlan}
          copy={copy}
          copyPlanDates={copyPlanDates}
        />
      ),
      "create-meal-planner-food": <AddFoodModal locals={locals} api={api} />, //main modal
      "handle-meal-planner-food": <HandleFoodModal locals={locals} />, //create-edit a food
      "create-new-food": <CreateNewFoodModal />, //modal to show 2 options (create a food or meal)
      "duplicate-meal-plan": <DuplicateMealPlanModal locals={locals} />, //duplicate a meal plan
      "move-meal-plan": <MoveMealPlanModal locals={locals} />, //move a meal plan
      "custom-meal": <CustomMealModal locals={locals} />, //modal to create cutom meals (something different as "snacks", "lunch", "breakfast")
      "build-meal": <BuildMealModal locals={locals} api={api} />, //create and edit a meal
      "save-as-template": <SaveMealPlanAsTemplateModal locals={locals} />,
      "data-source": <DataSourceModal locals={locals} />,
      "activity-feed": <ActivityDrawer />,
      "copy-meal-plan-from-template": (
        <CopyMealPlanFromTemplateModal locals={locals} />
      ),
    }[modal.type];

    const sections = {
      main: <Nutrition locals={locals} />,
      "meal-planner": <MealPlanner locals={locals} />,
    };

    res.body = sections[query.section || "main"];
  });
};

export default NutritionController;
